<script setup>
import { useCommonStore } from '../stores/common';
import { pageTransition } from '~/helpers/animations/animations';

const elementsRefs = ref([]);
const elementsVisibility = ref([]);
let observer = false;

const store = useCommonStore();
const gtm = useGtm();
const setI18nParams = useSetI18nParams();
const { setSidebarPhoto } = store;

definePageMeta({
  pageTransition: pageTransition,
});

const localePath = useLocalePath();
const route = useRoute();
const { t } = useI18n();

import eventQuery from '~/assets/gql/single-event.gql';

const { data } = await useAsyncQuery(eventQuery, { educationId: 39, eventId: route.params.id });

const event = computed(() => data.value?.event);
const bgColor = computed(() => event.value?.page_background_color);
const isSchoolCategory = computed(() => {
  return data.value.event.categories.some((category) => category.id == 14 || category.id == 15 || category.id == 13);
});

const breadcrumbs = ref([
  {
    title: t('pages.events.breadcrumbs-title'),
    route: 'events',
  },
  { title: t('pages.event-detail.breadcrumbs-title') },
]);

const content = ref(event?.value && event.value?.content ? JSON.parse(event.value?.content).blocks : []);

function getBuilding(obj) {
  if (obj && obj.buildings) {
    return obj.buildings;
  } else if (event.value && event.value?.buildings) {
    return event.value?.buildings;
  } else {
    return null;
  }
}

const forKids = () => {
  const kidsCategories = [9, 11, 32, 34];
  return event.value?.categories.find((category) => kidsCategories.includes(category.id));
};

const categories = () => {
  let categories = null;
  if (event.value?.categories) {
    categories = event.value?.categories;
    if (event.value?.categories.filter((category) => category.id == 14 || category.id == 15).length == 2) {
      categories = event.value?.categories.filter((category) => category.id != 15);
    }
  }
  return categories;
};

setI18nParams({
  en: {
    slug: event?.value?.slug?.en,
  },
  cs: {
    slug: event?.value?.slug?.cs,
  },
});

store.setPageColor(bgColor.value);
onMounted(() => {
  if (process.browser) {
    const goOutScript = document.createElement('script');
    goOutScript.setAttribute('src', 'https://partners.goout.net/cz-prague/ngpraguecz_new.js');
    goOutScript.async = true;
    goOutScript.onload = () => {
      console.log('GoOut script loaded');
      document.dispatchEvent(new Event('page-refreshed'));
    };
    document.body.appendChild(goOutScript);
  } else {
    console.log('GoOut script already exists');
    document.dispatchEvent(new Event('page-refreshed'));
  }

  store.setPageColor(bgColor.value);

  if (event && event.value?.cropped_detail_image) {
    setSidebarPhoto(event.value?.cropped_detail_image);
  }

  gtm.trackEvent({
    event: 'eventDetail_visit',
    eventId: route.params.id,
    url: window.location.href.replace(window.location.origin, ''),
  });

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const index = elementsRefs.value.indexOf(entry.target);
        if (index !== -1 && entry.isIntersecting) {
          elementsVisibility.value[index] = true;
        }
      });
    },
    { threshold: 0.08 }
  );

  elementsRefs.value.forEach((el) => {
    if (el) {
      observer.observe(el);
    }
  });
});
onBeforeRouteLeave(() => {
  store.setPageColor(null);
});

onBeforeUnmount(() => {
  setSidebarPhoto(null);
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

useHeadData({
  title: event.value?.seo_meta_title || event.value?.name || null,
  description: event.value?.seo_meta_description || null,
  keywords: event.value?.seo_meta_keywords || null,
  image: event.value?.image_detail?.url || null,
  url: route.fullPath,
});
</script>

<template>
  <main v-if="event" :style="{ backgroundColor: bgColor }" class="main main--event-detail">
    <LocalWarning v-if="event && event.warnings.local" :text="useBlocks(event.warnings.local)[0].data.text" :color="event.warnings?.local_color" :textcolor="event.warnings?.local_text_color" />
    <div class="event-detail">
      <div class="event-detail__header">
        <Breadcrumbs :items="breadcrumbs" />
        <div v-if="event.id == 3394" :ref="(el) => (elementsRefs[0] = el)" class="event-detail__hero mb-4 visible animate__animated animate__fadeInUp">
          <picture>
            <source type="image/gif" media="(min-width: 992px)" srcset="/img/flower-union/hero.gif" />
            <img :src="useGetImageUrl(event.image_mobile, null, 600, 'jpg')" :alt="event.image_detail_description" :title="event.image_detail_description" class="event-detail__hero-image" />
          </picture>
        </div>
        <div v-else :ref="(el) => (elementsRefs[0] = el)" class="event-detail__hero mb-4 visible animate__animated animate__fadeInUp">
          <img
            :src="useGetImageUrl(event.image_mobile || event.image_detail?.url || event.image_list?.url || '/img/dummy/ngp.png', 600)"
            class="event-detail__hero-image event-detail__hero-image--mobile"
            :title="event.image_detail_description || event.image_list_description"
            :alt="event.image_mobile_description || event.image_detail_description || event.image_list_description || event.name"
          />
          <img
            :src="useGetImageUrl(event.image_detail?.url || event.image_mobile || event.image_list?.url || '/img/dummy/ngp.png', 600)"
            class="event-detail__hero-image event-detail__hero-image--desktop"
            :title="event.image_detail_description || event.image_list_description"
            :alt="event.image_detail_description || event.image_mobile_description || event.image_list_description || event.name"
          />
        </div>
        <div
          :ref="(el) => (elementsRefs[1] = el)"
          class="event-detail__categories text text--2 fwm upper"
          :class="{
            'visible animate__animated animate__fadeInUp': elementsVisibility[1],
            invisible: !elementsVisibility[1],
          }"
        >
          <EventCategories v-if="categories && categories.length" :categories="categories" />
        </div>

        <span v-if="isSchoolCategory" class="event-detail__school-badge">Pro školy</span>

        <h1
          :ref="(el) => (elementsRefs[2] = el)"
          class="event-detail__heading heading container-max-content"
          :class="{
            'visible animate__animated animate__fadeInUp': elementsVisibility[2],
            invisible: !elementsVisibility[2],
          }"
          v-html="event.name"
        />

        <p v-if="event.program_type || event.school_degree_type" class="text text--2 mt-2">
          <template v-if="event.program_type">
            <strong>{{ t('pages.event-detail.program_type') }}: </strong>
            <template v-for="(type, key) in event.program_type"><template v-if="key > 0">, </template>{{ t('pages.event-detail.program_type.' + type) }}</template>
          </template>
          <br v-if="event.program_type && event.school_degree_type" />
          <template v-if="event.school_degree_type">
            <strong>{{ t('pages.event-detail.school_degree_type') }}: </strong>
            <template v-for="(type, key) in event.school_degree_type"><template v-if="key > 0">, </template>{{ t('pages.event-detail.school_degree_type.' + type) }}</template>
          </template>
        </p>
      </div>
      <div v-if="content" class="container-max-content">
        <div :ref="(el) => (elementsRefs[3] = el)">
          <div
            v-if="event.dates.length && !isSchoolCategory"
            class="event-detail__meta"
            :class="{
              'visible animate__animated animate__fadeInUp': elementsVisibility[3],
              invisible: !elementsVisibility[3],
            }"
          >
            <!-- <SlideTransition id="extraDates"></SlideTransition> -->
            <div class="event-detail__extra-dates">
              <div v-for="(item, key) in event.dates" :key="key" class="event-detail__extra-date">
                <EventDateStrip
                  :id="item.id"
                  :reservation-type="event.reservation_type"
                  :date="item || null"
                  :time="event.type == 'event' ? item?.start_at?.timestamp : null"
                  :time-stamp-end="item?.end_at?.timestamp || null"
                  :event-name="event.name"
                  :category-id="useGetCategoryId(event)"
                  :bold="true"
                  :locations="!event.online ? getBuilding(item) : null"
                  :no-link="!event.admission"
                  :no-button="event.admission?.is_hidden"
                  :type="event.type"
                  :is-school-type="isSchoolCategory"
                  :duration-display="event?.display_duration || false"
                  :hide-building-opening="event?.hide_building_opening || null"
                />
              </div>
            </div>
          </div>

          <div
            v-if="isSchoolCategory"
            class="event-detail__meta"
            :class="{
              'visible animate__animated animate__fadeInUp': elementsVisibility[3],
              invisible: !elementsVisibility[3],
            }"
          >
            <!-- <SlideTransition id="extraDates"></SlideTransition> -->
            <div class="event-detail__extra-dates">
              <div v-for="(item, key) in event.dates" :key="key" class="event-detail__extra-date">
                <EventDateStrip
                  :id="item.id"
                  :reservation-type="event.reservation_type"
                  :date="item || null"
                  :time="event.type == 'event' ? item?.start_at?.timestamp : null"
                  :time-stamp-end="item?.end_at?.timestamp || null"
                  :event-name="event.name"
                  :category-id="useGetCategoryId(event)"
                  :bold="true"
                  :locations="!event.online ? getBuilding(item) : null"
                  :no-link="!event.admission"
                  :no-button="event.admission?.is_hidden"
                  :type="event.type"
                  :is-school-type="isSchoolCategory"
                  :duration-display="event?.display_duration || false"
                  :hide-building-opening="event?.hide_building_opening || null"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-if="isSchoolCategory" :ref="(el) => (elementsRefs[4] = el)">
          <div
            v-if="forKids"
            class="event-detail__fb mt-4"
            :class="{
              'visible animate__animated animate__fadeInUp': elementsVisibility[4],
              invisible: !elementsVisibility[4],
            }"
          >
            <div class="event-detail__info-banner">
              <div class="info-item">
                <img src="/img/brushes.svg" alt="Brushes Icon" width="23" height="22" />
                <div class="info-content">
                  <h2>{{ t('pages.event-detail.target_group') }}</h2>
                  <span>{{ event.target_group }} </span>
                </div>
              </div>
              <div class="info-item">
                <img src="/img/clock.svg" alt="Clock Icon" width="23" height="22" />
                <div class="info-content">
                  <h2>{{ $t('pages.event-detail.duration') }}</h2>
                  <span>{{ event.duration }}</span>
                </div>
              </div>
              <div class="info-item">
                <img src="/img/group.svg" alt="Group Icon" width="34" height="23" />
                <div class="info-content">
                  <h2>{{ t('pages.event-detail.capacity') }}</h2>
                  <span>{{ event.capacity }} </span>
                </div>
              </div>
              <div class="info-item">
                <img src="/img/coin.svg" alt="Coin Icon" width="23" height="22" />
                <div class="info-content">
                  <h2>{{ t('pages.event-detail.price') }}</h2>

                  <span>{{ event.price }} </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DynamicPageContent :content="content" class="mt-4 hh" />
        <FlowerUnion v-if="event.id === 3394" />

        <div v-if="isSchoolCategory" :ref="(el) => (elementsRefs[5] = el)">
          <div
            v-if="forKids"
            class="event-detail__fb mt-4"
            :class="{
              'visible animate__animated animate__fadeInUp': elementsVisibility[5],
              invisible: !elementsVisibility[5],
            }"
          >
            <div id="program-reservation" class="event-detail__reservation">
              <h2>Rezervace programu</h2>

              <div id="goout"></div>
            </div>
          </div>
        </div>

        <div :ref="(el) => (elementsRefs[6] = el)">
          <div
            v-if="forKids"
            class="event-detail__fb mt-4"
            :class="{
              'visible animate__animated animate__fadeInUp': elementsVisibility[6],
              invisible: !elementsVisibility[6],
            }"
          >
            <a href="https://www.facebook.com/NGPrague" target="_blank">
              <span class="icon-facebook" />
              <span class="facebook-text">Facebook Národní galerie Praha</span>
            </a>
          </div>
        </div>
        <div :ref="(el) => (elementsRefs[6] = el)">
          <div
            v-if="event.tags.length"
            class="event-detail__tags mt-5"
            :class="{
              'visible animate__animated animate__fadeInUp': elementsVisibility[7],
              invisible: !elementsVisibility[7],
            }"
          >
            <h2 class="heading heading--2 heading--thin">
              {{ t('pages.event-detail.tags.title') }}
            </h2>
            <ul class="event-detail__tags mt-3">
              <li v-for="(tag, key) in event.tags" :key="key" class="event-detail__tag">
                <nuxt-link
                  :to="
                    localePath({
                      name: 'events',
                      params: { filter: tag.slug[locale] },
                    })
                  "
                  class="link"
                >
                  {{ tag.name }}
                </nuxt-link>
              </li>
            </ul>
          </div>
        </div>
        <div :ref="(el) => (elementsRefs[8] = el)">
          <div v-show="event.related.length" class="mt-5 visible animate__animated animate__fadeInUp">
            <!-- :class="{
              'visible animate__animated animate__fadeInUp': elementsVisibility[6],
              invisible: !elementsVisibility[6],
            }" -->
            <h2 class="heading heading--2 heading--thin">
              {{ t('pages.event-detail.related.title') }}
            </h2>
            <EventGrid :data="event.related" class="mt-3" />
          </div>
        </div>

        <div :ref="(el) => (elementsRefs[9] = el)">
          <ContactList
            v-if="event.contacts.length"
            :contacts="event.contacts"
            :class="{
              'visible animate__animated animate__fadeInUp': elementsVisibility[9],
              invisible: !elementsVisibility[9],
            }"
          />
        </div>
        <div :ref="(el) => (elementsRefs[10] = el)">
          <DocumentList
            v-if="event.documents.length"
            :documents="event.documents"
            :class="{
              'visible animate__animated animate__fadeInUp': elementsVisibility[10],
              invisible: !elementsVisibility[10],
            }"
          />
        </div>
        <div :ref="(el) => (elementsRefs[11] = el)">
          <div
            v-if="event.partners.length"
            class="mt-5"
            :class="{
              'visible animate__animated animate__fadeInUp': elementsVisibility[11],
              invisible: !elementsVisibility[11],
            }"
          >
            <h2 class="heading heading--2 heading--thin">
              {{ t('pages.event-detail.partners.title') }}
            </h2>

            <LogoGrid :data="event.partners" class="mt-3" />
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.event-detail {
  &__header {
    padding-bottom: 12px;
    @include mq($sm) {
      display: flex;
      flex-flow: column;
      margin-top: #{$mainPaddingTop * -1};
      min-height: 100vh;
      padding-top: $mainPaddingTop;
    }

    @include mq($lg) {
      margin-top: #{$mainLgPaddingTop * -1};
      padding-top: $mainLgPaddingTop;
    }
  }

  &__school-badge {
    color: #000;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 22px; /* 137.5% */
    text-transform: uppercase;

    margin-bottom: 6px;
  }

  &__heading {
    flex-grow: 0;
  }

  &__info-banner {
    display: flex;
    justify-content: space-between;
    width: 100%;

    border-bottom: 1px solid #000;
    padding-bottom: 34px;

    .info-item {
      display: flex;
      gap: 8px;
      width: max-content;
      max-width: 248px;

      .info-content {
        display: flex;
        flex-direction: column;
        gap: 7px;
        width: max-content;
        max-width: 248px;

        h2,
        span {
          color: #000;
          font-size: 16px;
          line-height: 22px;
          width: max-content;
        }

        h2 {
          font-weight: 500;
          width: max-content;
        }

        span {
          font-style: normal;
          font-weight: 400;
          text-wrap: balance;
          width: max-content;
          max-width: 204px;
        }
      }

      &:nth-child(1) span {
        max-width: 113px;
      }
      &:nth-child(2) span {
        max-width: 68px;
      }
      &:nth-child(3) span {
        max-width: 133px;
      }
      &:nth-child(4) span {
        max-width: 190px;
      }
    }

    @media screen and (max-width: 1270px) {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
  }

  &__reservation {
    display: flex;
    flex-direction: column;
    gap: 20px;

    padding-top: 10px;
    padding-bottom: 40px;
    padding-top: 50px;

    border-bottom: 1px solid #000;
    border-top: 1px solid #000;

    h2 {
      color: #000;
      font-size: 30px;
      font-style: normal;
      font-weight: 400;
      line-height: 36px;
      letter-spacing: -0.4px;
    }
  }

  &__meta {
    border-bottom: 1px solid $black;
    border-top: 1px solid $black;
    margin-bottom: 40px;
    margin-top: 40px;
    // padding-bottom: 15px;

    &:first-child {
      margin-top: 0;
    }

    .event-detail__extra-date {
      &:last-child {
        border-bottom: 0;
      }
    }
  }

  &__hero {
    overflow: hidden;
    @include mq($sm) {
      flex-grow: 1;
      min-height: 50vh;
      // overflow-x: auto;
      position: relative;
    }

    @include mq($md) {
      min-height: 0;
    }
  }

  &__hero-image {
    display: block;
    width: 100%;
    overflow: hidden;

    @include mq($sm) {
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      object-position: left top;
      object-fit: contain;
      width: auto;
    }
    @include mq($lg) {
      object-position: center;
      object-fit: cover;
    }
  }

  &__tags {
  }

  &__tags-title {
    font-weight: 500;
    text-transform: uppercase;
  }

  &__tags-list {
    display: inline-block;
  }

  &__tag {
    display: inline-block;

    &:after {
      content: ', ';
    }

    &:last-child {
      &:after {
        display: none;
      }
    }
  }

  &__people {
  }

  &__person {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__fb {
    a {
      .facebook-text {
        text-decoration: underline;
      }

      &:hover {
        .facebook-text {
          text-decoration: none;
        }
      }
    }
    span {
      display: inline-block;
      vertical-align: middle;
    }
    .icon-facebook {
      margin-right: 12px;
      font-size: 32px;
    }
  }
}

.hh {
  position: relative;
  z-index: 4;
}
.b-flex-child {
  min-width: 100px !important;
}

.event-detail__hero-image--desktop {
  @media (max-width: 992px) {
    display: none;
  }
}

.event-detail__hero-image--mobile {
  @media (min-width: 993px) {
    display: none;
  }
}
</style>
